/**
 * @fileoverview Analysis wizard content controller component
 */

import React, { useState, useRef } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import TrebbleButton from '../../common/TrebbleButton.tsx';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import TrebbleSteps from '../../common/TrebbleSteps.tsx';
import { useAnalysisWizard } from '../hooks/useAnalysisWizard.js';
import deepCutApi from '../services/deepcutApi.js';
import GoalStep from './GoalStep.jsx';
import QuestionsStep from './QuestionsStep.jsx';
import PlanStep from './PlanStep.jsx';
import PatternReviewStep from './PatternReviewStep.jsx';
import AnalysisLoadingSkeleton from './AnalysisLoadingSkeleton.jsx';
import ContinueLoadingSkeleton from './ContinueLoadingSkeleton.jsx';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 * @typedef {import('../types/deepcut.js').UserFeedback} UserFeedback
 */

/**
 * Analysis wizard content controller
 * @param {Object} props - Component props
 * @param {AnalysisResults|null} props.results - Analysis results
 * @param {string} props.transcript - The transcript text
 * @param {number|null} props.transcriptId - ID of the transcript (if saved)
 * @param {Function} props.onClose - Callback for closing the wizard
 * @param {Function} props.onApply - Callback for applying the final plan
 * @param {boolean} [props.isUpdating] - Whether plan is being updated
 * @param {boolean} [props.isLoading] - Whether analysis is loading
 * @param {Function} [props.renderFooter] - Optional custom footer renderer
 * @returns {React.ReactElement}
 */
export default function AnalysisWizardContent({
  results,
  transcript,
  transcriptId,
  projectId,
  onClose,
  onApply,
  isUpdating = false,
  isLoading = false,
  renderFooter,
  isEditLikeMeMode = false,
}) {
  // In ELM mode, prefetch reference projects during the shimmer so the picker is instant
  const [prefetchedProjects, setPrefetchedProjects] = useState(null);
  const [initialAnalysisLoading, setInitialAnalysisLoading] = useState(!results && !isEditLikeMeMode);

  React.useEffect(() => {
    if (isEditLikeMeMode && !prefetchedProjects) {
      import('../../../models/helper/TrebbleClientAPI').then(mod => {
        const api = mod.default.getInstance();
        api.getEditedProjectsForUser(50, 0, '').then(result => {
          setPrefetchedProjects(result?.projects || []);
        }).catch(() => setPrefetchedProjects([]));
      });
    }
  }, [isEditLikeMeMode, prefetchedProjects]);

  // In ELM mode: show shimmer until projects are loaded, then render goal step immediately
  const elmReady = !isEditLikeMeMode || prefetchedProjects !== null;
  const [analysisResults, setAnalysisResults] = useState(results || (isEditLikeMeMode ? { plan: [], probingQuestions: [] } : null));
  const patternReviewRef = useRef(null);
  
  // Sync analysisResults with results prop when it changes
  React.useEffect(() => {
    if (results) {
      setAnalysisResults(results);
    }
  }, [results]);
  
  const performInitialAnalysis = React.useCallback(async () => {
    try {
      setInitialAnalysisLoading(true);

      // Create initial analysis request
      const requestData = {
        transcript: transcript,
        transcriptionId: transcriptId,
        projectId: projectId,
        userDirectives: [],
        selectedIntent: '',
        targetPlatform: '',
        selectedContentType: '',
        userFlow: {
          hasSeenQuestions: false,
          isAnsweringQuestions: false,
          isUpdatingGoals: false
        },
        isEditLikeMeMode: isEditLikeMeMode,
      };

      const result = await deepCutApi.analyzeTranscript(requestData);

      setAnalysisResults(result);
    } catch (error) {
      console.error('Initial analysis failed:', error);
      // Show error or fallback
    } finally {
      setInitialAnalysisLoading(false);
    }
  }, [transcript, transcriptId, projectId, isEditLikeMeMode]);

  // Perform initial analysis if no results provided
  // Skip in Edit Like Me mode — analysis should only happen after user picks a reference project
  React.useEffect(() => {
    if (!results && transcript && !analysisResults && !isEditLikeMeMode) {
      performInitialAnalysis();
    }
  }, [results, transcript, analysisResults, performInitialAnalysis, isEditLikeMeMode]);

  // Handle execute plan - directly apply without confirmation modal
  const handleExecutePlan = async () => {
    await wizard.handleApply();
  };

  // Always call the hook - handle null results inside the hook
  const wizard = useAnalysisWizard({
    results: analysisResults || null,
    transcript,
    transcriptId,
    projectId,
    onClose,
    onApply,
    isEditLikeMeMode,
  });

  const renderCurrentStep = () => {
    if (isLoading || initialAnalysisLoading || !analysisResults || !elmReady) {
      return <AnalysisLoadingSkeleton />;
    }

    // Show continue loading skeleton when refreshing between steps
    if (wizard.isRefreshing) {
      return <ContinueLoadingSkeleton
        currentStep={wizard.currentStep}
        progressMessage={wizard.synthesisProgressMessage}
      />;
    }

    switch (wizard.currentStep) {
      case 'goal':
        return (
          <GoalStep
            key="goal-step"
            results={wizard.localResults}
            selectedIntent={wizard.selectedIntent}
            customIntent={wizard.customIntent}
            userDirectivesText={wizard.userDirectivesText}
            targetPlatform={wizard.targetPlatform}
            customPlatform={wizard.customPlatform}
            selectedContentType={wizard.selectedContentType}
            customContentType={wizard.customContentType}
            onSelectedIntentChange={wizard.setSelectedIntent}
            onCustomIntentChange={wizard.setCustomIntent}
            onUserDirectivesChange={wizard.setUserDirectivesText}
            onTargetPlatformChange={wizard.setTargetPlatform}
            onCustomPlatformChange={wizard.setCustomPlatform}
            onSelectedContentTypeChange={wizard.setSelectedContentType}
            onCustomContentTypeChange={wizard.setCustomContentType}
            isEditLikeMeMode={isEditLikeMeMode}
            selectedReferenceProject={wizard.selectedReferenceProject}
            onReferenceProjectChange={wizard.handleReferenceProjectChange}
            prefetchedProjects={prefetchedProjects}
          />
        );
      case 'patternReview':
        return (
          <PatternReviewStep
            ref={patternReviewRef}
            key="pattern-review-step"
            referenceEditingPatterns={wizard.cachedReferenceEditingPatterns}
            synthesisResult={wizard.synthesisResult}
            onApprove={wizard.handlePatternReviewApprove}
            onBack={wizard.handleBack}
          />
        );
      case 'questions':
        return (
          <QuestionsStep
            key="questions-step"
            workingQuestions={wizard.workingQuestions}
            questionAnswers={wizard.questionAnswers}
            onQuestionAnswersChange={wizard.setQuestionAnswers}
          />
        );
      case 'plan':
        return (
          <PlanStep
            key="plan-step"
            localResults={wizard.localResults}
            targetPlatform={wizard.targetPlatform}
            customPlatform={wizard.customPlatform}
            questionAnswers={wizard.questionAnswers}
            planSelections={wizard.planSelections}
            onPlanSelectionsChange={wizard.setPlanSelections}
            referenceEditingPatterns={
              isEditLikeMeMode
                ? (wizard.localResults?.referenceEditingPatterns || wizard.cachedReferenceEditingPatterns)
                : null
            }
            onPatternsChange={isEditLikeMeMode ? wizard.setModifiedPatterns : null}
          />
        );
      default:
        return null;
    }
  };

  // Helper function to get step-specific loading message
  const getContinueLoadingMessage = () => {
    if (wizard.currentStep === 'goal') {
      return (window.getI18n && window.getI18n(ti18n, 'ANALYZING_YOUR_CONTENT')) || 'Analyzing your content...';
    } else if (wizard.currentStep === 'questions') {
      return (window.getI18n && window.getI18n(ti18n, 'PROCESSING_YOUR_ANSWERS')) || 'Processing your answers...';
    }
    return (window.getI18n && window.getI18n(ti18n, 'PLEASE_WAIT_ANALYZING')) || 'Please wait while we analyze your input';
  };

  const defaultFooter = () => {
    if (isLoading || initialAnalysisLoading || !analysisResults) {
      return (
        <TrebbleSpace style={{ width: '100%', justifyContent: 'flex-end' }}>
          <TrebbleButton onClick={onClose}>{window.getI18n(ti18n, 'CANCEL')}</TrebbleButton>
        </TrebbleSpace>
      );
    }

    // Show only Cancel button when refreshing between steps
    if (wizard.isRefreshing) {
      return (
        <TrebbleSpace className="w-full justify-end">
          <TrebbleButton onClick={onClose}>{window.getI18n(ti18n, 'CANCEL')}</TrebbleButton>
        </TrebbleSpace>
      );
    }

    // Pattern Review step — this IS the plan in Edit Like Me mode
    if (wizard.currentStep === 'patternReview') {
      return (
        <TrebbleSpace className="w-full justify-between">
          <TrebbleButton
            icon={<ArrowLeftOutlined style={{ color: 'var(--trebble-primary)' }} />}
            onClick={wizard.handleBack}
          >
            {window.getI18n(ti18n, 'BACK')}
          </TrebbleButton>
          <TrebbleSpace>
            <TrebbleButton
              onClick={() => patternReviewRef.current?.reset()}
            >
              {window.getI18n(ti18n, 'RESET_ALL') || 'Reset'}
            </TrebbleButton>
            <TrebbleButton
              type="primary"
              onClick={async () => {
                patternReviewRef.current?.approve();
                await wizard.handleApply();
              }}
              loading={isUpdating}
            >
              {window.getI18n(ti18n, 'EXECUTE_PLAN')}
            </TrebbleButton>
          </TrebbleSpace>
        </TrebbleSpace>
      );
    }

    return (
      <TrebbleSpace className="w-full justify-between">
        <TrebbleButton
          icon={<ArrowLeftOutlined style={{ color: 'var(--trebble-primary)' }} />}
          onClick={wizard.handleBack}
          disabled={wizard.currentStep === 'goal'}
        >
          {window.getI18n(ti18n, 'BACK')}
        </TrebbleButton>
        <TrebbleSpace>
          <TrebbleButton onClick={onClose}>{window.getI18n(ti18n, 'CANCEL')}</TrebbleButton>
          {wizard.currentStep === 'plan' ? (
            <TrebbleButton
              type="primary"
              onClick={handleExecutePlan}
              loading={isUpdating}
            >
              {window.getI18n(ti18n, 'EXECUTE_PLAN')}
            </TrebbleButton>
          ) : (
            <TrebbleButton
              type="primary"
              icon={<ArrowRightOutlined style={{ color: 'white' }} />}
              onClick={wizard.handleNext}
              disabled={!wizard.canProceed()}
            >
              {window.getI18n(ti18n, 'CONTINUE')}
            </TrebbleButton>
          )}
        </TrebbleSpace>
      </TrebbleSpace>
    );
  };

  const footerProps = analysisResults ? {
    currentStep: wizard.currentStep,
    canProceed: wizard.canProceed(),
    isRefreshing: wizard.isRefreshing,
    onBack: wizard.handleBack,
    onNext: wizard.handleNext,
    onApply: wizard.handleApply,
    onClose,
    isUpdating
  } : {
    currentStep: '',
    canProceed: false,
    isRefreshing: false,
    onBack: () => {},
    onNext: () => {},
    onApply: () => {},
    onClose,
    isUpdating
  };

  return (
    <>
      <div className="max-w-[800px] mx-auto p-6 bg-[var(--bg-default)] rounded-none min-h-[500px]">
        <div className="min-h-[400px] py-4">
          {!isLoading && !initialAnalysisLoading && analysisResults && (
            <TrebbleSteps
              current={wizard.stepIndex}
              className="mb-6"
              items={(() => {
                // In Edit Like Me mode, the pattern review IS the plan — just 2 steps
                if (isEditLikeMeMode) {
                  const elmSteps = [{ title: window.getI18n(ti18n, 'STEP_SET_UP') }];
                  if (wizard.hasEverHadQuestions) {
                    elmSteps.push({ title: window.getI18n(ti18n, 'STEP_REFINE') });
                  }
                  elmSteps.push({ title: window.getI18n(ti18n, 'STEP_PLAN') });
                  return elmSteps;
                }
                const steps = [{ title: window.getI18n(ti18n, 'STEP_SET_UP') }];
                if (wizard.hasEverHadQuestions) {
                  steps.push({ title: window.getI18n(ti18n, 'STEP_REFINE') });
                }
                steps.push({ title: window.getI18n(ti18n, 'STEP_PLAN') });
                return steps;
              })()}
            />
          )}
          {renderCurrentStep()}
        </div>
        {renderFooter ? renderFooter(footerProps) : defaultFooter()}
      </div>
    </>
  );
} 